import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RequestDetailLayout } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestDetailLayout/RequestDetailLayout";
import { RequestDetailBox } from "@companion-core/shared/app/Components/Requests/RequestDetail/RequestDetailBox";
import { CancelPopIn } from "@companion-core/shared/app/Components/Requests/RequestDetail/CancelPopIn";
import { Button } from "@companion-core/shared/app/Components/Button";
import {
  RequestDetailAction,
  RequestDetailContent,
  RequestDetailFooterProps,
  RequestDetailProps,
} from "@companion-core/shared/app/Components/Requests/RequestDetail/requestDetail.model";
import {
  trackClickRequestDetailCTA,
  trackClickRequestRejectProposal,
  trackClickShowPreviousProposals,
  trackPageRequestDetail,
} from "@companion-core/shared/app/tracking/requests";
import { useProposals } from "@companion-core/shared/app/Queries";
import { APIProposal } from "@companion-core/shared/app/Interfaces/API";

export const RequestDetail = ({
  data,
  id,
  contentId,
  detailContent,
  navigateToDetailContent,
  navigateToDetailRecommendation,
  navigateToEditor,
  goBack,
}: RequestDetailProps) => {
  const { t } = useTranslation();
  const [isCancelPopInOpen, setIsCancelPopInOpen] = useState(false);
  const requestDetailContent = useMemo(
    () => detailContent ?? RequestDetailContent.detail,
    [detailContent]
  );
  const status = useMemo(
    () =>
      !data.has_digital_proposal && data.status === "recommended"
        ? "recommended-by-mail"
        : data.status,
    [data]
  );
  const hasDigitalProposal = useMemo(() => data.has_digital_proposal, [data]);
  const { data: proposalsData, isPending: isProposalsPending } = useProposals(
    data.id,
    hasDigitalProposal
  );

  const handleCTA = useCallback(
    ({
      state,
      proposalId = "",
      recommendationId = "",
    }: {
      state: string;
      proposalId?: string;
      recommendationId?: string;
    }) => {
      trackClickRequestDetailCTA({
        state: state as Parameters<typeof trackClickRequestDetailCTA>[0]["state"],
        family: data.family,
        subFamily: data.sub_family,
        vendorId: data.vendor?.vendor_id,
        requestId: data.id,
        status: data.status,
      });
      switch (state) {
        case "RequestCancel":
          setIsCancelPopInOpen(true);
          break;
        case "RequestUpdate":
          navigateToEditor?.(RequestDetailAction.modify);
          break;
        case "RequestProposals":
          navigateToDetailContent(RequestDetailContent.proposals);
          break;
        case "RequestRecommendations":
          navigateToDetailContent(RequestDetailContent.recommendations);
          break;
        case "RequestRecommendationDetail":
          navigateToDetailRecommendation(proposalId, recommendationId);
          break;
      }
    },
    [data, requestDetailContent, navigateToDetailContent]
  );

  // display page tracking
  useEffect(() => {
    trackPageRequestDetail({
      family: data.family,
      subFamily: data.sub_family,
      vendorId: data.vendor?.vendor_id,
      requestId: data.id,
      status: data.status,
    });
  }, []);

  const RequestDetailFooter = ({
    status,
    requestDetailContent,
    navigateToEditor,
  }: RequestDetailFooterProps) => {
    const proposalValidated = useMemo(() => {
      return proposalsData?.data?.results?.find(
        (proposal: APIProposal) => proposal.status == "validated"
      );
    }, [proposalsData]);
    const showDeclineCTA =
      requestDetailContent === RequestDetailContent.recommendations && status === "recommended";
    const showPreviousRecommendationsCTA =
      requestDetailContent === RequestDetailContent.detail &&
      status === "proposal-validated" &&
      proposalValidated?.recommendations?.length > 1 &&
      proposalsData?.data?.results?.length === 1;
    const showPreviousProposalsCTA =
      requestDetailContent === RequestDetailContent.detail &&
      ["recommended", "proposal-validated"].includes(status) &&
      proposalsData?.data?.results?.length > 1;

    return (
      <>
        {showDeclineCTA && (
          <Button
            testID="request-detail__decline-cta-link"
            type="link"
            classNames={["request-detail__footer-cta", "body-text-6"]}
            style={{ marginBottom: 32, paddingVertical: 8 }}
            label={t("request.my-requests.detail.button.decline.label")}
            onClick={() => {
              trackClickRequestRejectProposal({
                family: data?.family,
                subFamily: data?.sub_family,
                requestId: data?.id,
                status: data?.status,
              });
              navigateToEditor?.(RequestDetailAction.decline);
            }}
            onPress={() => {
              trackClickRequestRejectProposal({
                family: data?.family,
                subFamily: data?.sub_family,
                requestId: data?.id,
                status: data?.status,
              });
              navigateToEditor?.(RequestDetailAction.decline);
            }}
          />
        )}
        {showPreviousRecommendationsCTA && (
          <Button
            testID="request-detail__previous-recommendations-cta-link"
            type="link"
            classNames={["request-detail__footer-cta", "body-text-6"]}
            style={{ marginBottom: 32, paddingVertical: 8 }}
            label={t("request.my-requests.detail.button.previous-proposals.label")}
            onClick={() => {
              trackClickShowPreviousProposals({
                family: data?.family,
                subFamily: data?.sub_family,
                requestId: data?.id,
                status: data?.status,
              });
              navigateToDetailContent(RequestDetailContent.recommendations);
            }}
            onPress={() => {
              trackClickShowPreviousProposals({
                family: data?.family,
                subFamily: data?.sub_family,
                requestId: data?.id,
                status: data?.status,
              });
              navigateToDetailContent(RequestDetailContent.recommendations);
            }}
          />
        )}
        {showPreviousProposalsCTA && (
          <Button
            testID="request-detail__previous-proposals-cta-link"
            type="link"
            classNames={["request-detail__footer-cta", "body-text-6"]}
            style={{ marginBottom: 32, paddingVertical: 8 }}
            label={t("request.my-requests.detail.button.previous-proposals.label")}
            onClick={() => {
              // TODO tracking
              navigateToDetailContent(RequestDetailContent.proposals);
            }}
            onPress={() => {
              // TODO tracking
              navigateToDetailContent(RequestDetailContent.proposals);
            }}
          />
        )}
      </>
    );
  };

  return (
    <>
      {data && (!hasDigitalProposal || !isProposalsPending) && (
        <CancelPopIn
          isOpen={isCancelPopInOpen}
          requestData={data}
          onClose={() => setIsCancelPopInOpen(false)}
        />
      )}

      <RequestDetailLayout data={data} detailContent={requestDetailContent}>
        <>
          <RequestDetailBox
            requestData={data}
            proposalsData={proposalsData}
            isProposalsPending={isProposalsPending}
            id={id}
            contentId={contentId}
            requestDetailContent={requestDetailContent}
            performCancelRequest={() => handleCTA({ state: "RequestCancel" })}
            performUpdateRequest={() => handleCTA({ state: "RequestUpdate" })}
            performDisplayProposals={() => handleCTA({ state: "RequestProposals" })}
            performDisplayRecommendations={() => handleCTA({ state: "RequestRecommendations" })}
            performDisplayRecommendationDetail={(proposalId: string, recommendationId: string) =>
              handleCTA({ state: "RequestRecommendationDetail", proposalId, recommendationId })
            }
            goBack={goBack}
          />
          <RequestDetailFooter
            status={status}
            hasDigitalProposal={data.has_digital_proposal}
            requestDetailContent={requestDetailContent}
            navigateToEditor={navigateToEditor}
          />
        </>
      </RequestDetailLayout>
    </>
  );
};
